/**
 * DroughtWatch V2 -- API client
 * ==============================
 * Thin wrappers around the FastAPI backend endpoints (backend/app/main.py).
 * Every function here matches a route that was actually tested against the
 * real model + scaler -- see the conversation for the test transcript.
 *
 * Usage in the dashboard component:
 *   import { getHealth, predict, getMetrics, getForecastGrid, getAlerts } from "./api";
 */

const BASE_URL = import.meta.env?.VITE_API_BASE_URL || "/api/v2/clean";

async function request(path, options = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Request to ${path} failed with ${res.status}`);
  }
  return res.json();
}

/**
 * GET /feature-ranges
 * -> { ndvi: {min, max}, evi: {min, max}, lst_celsius: {min, max}, precip_mm: {min, max}, soil_moisture: {min, max} }
 * The EXACT bounds the scaler was fit with. Use these for slider/input
 * bounds in any What-If UI so a control can never send an out-of-range
 * value to /predict.
 */
export function getFeatureRanges() {
  return request("/feature-ranges");
}

/** GET /health -> { status, model_loaded, scaler_loaded, grid_loaded, trainable_params } */
export function getHealth() {
  return request("/health");
}

/**
 * POST /predict
 * history: array of 12 objects, oldest first:
 *   { ndvi, evi, lst_celsius, precip_mm, soil_moisture }
 * mcDropoutSamples: 0 for a fast deterministic forecast (no probability),
 *   30-100 for a slower MC-Dropout forecast WITH drought_probability per month.
 * -> { forecast: [{ month_offset, ndvi_forecast, drought_probability }], drought_threshold }
 */
export function predict(history, mcDropoutSamples = 50) {
  return request("/predict", {
    method: "POST",
    body: JSON.stringify({ history, mc_dropout_samples: mcDropoutSamples }),
  });
}

/** GET /metrics -> { baseline_mae, baseline_rmse, baseline_r2, model_mae, model_rmse, model_r2, ... } */
export function getMetrics() {
  return request("/metrics");
}

/**
 * GET /forecast/grid?month=1..6
 * -> { month_offset, height, width, ndvi_grid: number|null [height][width] }
 * Requires grid_mask.npz + forecast_grid.npy to be present in backend/artifacts/.
 */
export function getForecastGrid(month = 1) {
  return request(`/forecast/grid?month=${month}`);
}

/**
 * GET /forecast/grid/all
 * -> { n_months, height, width, grids: number|null [n_months][height][width] }
 * Fetch once on load, cache client-side, and scrub the month slider with
 * zero further network requests. Same file requirement as getForecastGrid.
 */
export function getForecastGridAll() {
  return request("/forecast/grid/all");
}

/**
 * GET /forecast/pixel?row=...&col=...
 * Returns strictly monotonic quantile trajectory (P10, P50, P90) for a specific pixel.
 */
export function getForecastPixel(row, col) {
  return request(`/forecast/pixel?row=${row}&col=${col}`);
}

/**
 * GET /alerts?month=1..6&top_n=10
 * -> { month_offset, hotspots: [{ pixel_id, row, col, lon, lat, drought_probability, ndvi_forecast, p10, p50, p90 }] }
 * Same file requirement as getForecastGrid.
 */
export function getAlerts(month = 1, topN = 10) {
  return request(`/alerts?month=${month}&top_n=${topN}`);
}

